import React, { useState, useEffect } from "react";
import { api } from "../../api/api";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import transformCountryWithCities from "./utils/transformCountryWithCities";
import FilterBar from "./components/FilterBar/FilterBar";
import { Button, Panel } from "@epam/uui";
import { useUuiContext } from "@epam/uui-core";
import { ReactComponent as ActionAddOutlineIcon } from "@epam/assets/icons/action-add-outline.svg";
import { ReactComponent as NotificationInfoOutlineIcon } from "@epam/assets/icons/notification-info-outline.svg";
import CourierCreate from "../dialog/couriers/CourierDetailView";
import CourierCardList from "./components/CourierCardList/CourierCardList";
import "./CourierView.css";
import UserGuideModal from "./components/UserGuideModal/UserGuideModal";
import { ModuleHeader } from "../../../components/composed";
import { FadeIn } from "../../../components/motion";

interface Props {
  snackBarAddMessage: (message: string, type: string) => void;
  onFetchFailed: (error: any) => void;
  user: { id: number; roles: string[]; name: string };
  courieId?: number;
}

export interface DialogCourier {
  id: number;
  courierType: string;
  destination: string;
  destinationType: string;
  details: string;
  source: string;
  creator: { id?: number; name?: string };
}

const CouriersView: React.FC<Props> = (props) => {
  const theme = useTheme();
  const [sizes, setSizes] = useState([]);
  const [countries, setCountries] = useState<{ id: string; name: string }[]>(
    []
  );
  const [cityList, setCityList] = useState([]);
  const [filter, setFilter] = useState({});

  const [updatedCourier, setUpdatedCourier] = useState({});

  const svc = useUuiContext();

  const fetchAllData = () => {
    async function fetchDataFromAPI() {
      try {
        const sizeResponse = await api.dictionary.sizeAll();
        setSizes(sizeResponse.content);

        const [countryResponse, cityResponse] = await Promise.all([
          api.dictionary.countryAll(),
          api.dictionary.locationAll(),
        ]);

        const countryData = countryResponse.content;
        const cityData = cityResponse.content;
        setCityList(cityData);

        const mergedData = transformCountryWithCities(countryData, cityData);

        setCountries([
          ...mergedData,
          {
            countryCode: "other",
            name: "Other",
            id: "other",
            cityList: [],
          },
        ]);
      } catch (error) {
        props.onFetchFailed(error);
        props.snackBarAddMessage(`${error}`, "error");
      }
    }
    fetchDataFromAPI();
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const submitFilters = (filters: any) => {
    setFilter(filters);
  };

  return (
    <FadeIn>
      <ModuleHeader
        title="Couriers"
        subtitle="Send and deliver for fellow EPAMers"
        moduleColor={theme.palette.courier.main}
        actions={
          <>
            <Button
              color="secondary"
              fill="outline"
              caption="How to use"
              icon={NotificationInfoOutlineIcon}
              iconPosition="right"
              onClick={() =>
                svc.uuiModals.show((props) => <UserGuideModal {...props} />)
              }
            />
            <Button
              color="primary"
              caption="Create Request"
              icon={ActionAddOutlineIcon}
              iconPosition="right"
              onClick={() => {
                svc.uuiModals.show((props) => (
                  <CourierCreate
                    countries={countries}
                    sizes={sizes}
                    onUpdate={setUpdatedCourier}
                    cityList={cityList}
                    {...props}
                  />
                ));
              }}
            />
          </>
        }
      />

      <Panel cx="container" style={{ marginTop: 16 }}>
        <FilterBar
          onSubmit={submitFilters}
          countryList={countries}
          sizeList={sizes}
        />
        <CourierCardList
          sizeList={sizes}
          countryList={countries}
          user={props.user}
          updatedCourier={updatedCourier}
          onCourierUpdate={setUpdatedCourier}
          filter={filter}
          cityList={cityList}
          courierId={props.courieId}
        />
      </Panel>
    </FadeIn>
  );
};
export default CouriersView;
